<template>
  <div>
    <div class="flex justify-between items-center">
      <BotEditorSubHeader
        :title="$t('botEditor.flows.title')"
        :subtitle="$t('botEditor.flows.subtitle')"
      />
      <Button id="btn-request-create-flow" severity="primary" @click="openCreateModal">
        <span class="material-icons">add</span>
        {{ $t('botEditor.flows.createButton') }}
      </Button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center text-gray-400 py-12">
      <div class="flex justify-center mb-3">
        <ProgressSpinner
          style="width: 2rem; height: 2rem"
          stroke-width="3"
          :aria-label="$t('common.loading')"
        />
      </div>
      {{ $t('botEditor.flows.loadingText') }}
    </div>

    <!-- Empty state -->
    <div v-else-if="flows.length === 0" class="bg-gray-800 rounded-lg">
      <div class="text-center text-gray-400 p-8">{{ $t('botEditor.flows.emptyState') }}</div>
    </div>

    <!-- Flow list -->
    <div v-else id="flows-list" class="flex flex-col gap-3">
      <router-link
        v-for="flow in flows"
        :key="flow._id"
        :to="`/app/workspace/${route.params.workspaceId}/bots/${route.params.id}/flows/editor/${flow._id}`"
        data-telemetry-id="flow-list-item"
        class="group block bg-gray-800 border border-gray-700 rounded-lg px-5 py-2.5 hover:border-cyan-700/60 hover:shadow-lg hover:shadow-black/20 transition-all cursor-pointer"
      >
        <div class="flex items-center justify-between gap-4">
          <!-- Left: icon + info -->
          <div class="flex items-start gap-3 flex-1 min-w-0">
            <div class="w-10 h-10 rounded-lg bg-cyan-900/40 border border-cyan-700/40 flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-cyan-900/60 transition-colors">
              <span class="material-icons text-cyan-400 text-lg">account_tree</span>
            </div>
            <div class="flex-1 min-w-0">
              <!-- Name + badge -->
              <div class="flex items-center gap-2 flex-wrap">
                <h3 class="font-semibold text-white text-base">{{ flow.name }}</h3>
                <Chip
                  v-if="flow.isValid === false"
                  class="!bg-red-500/15 !text-red-400 !border !border-red-500/30 !text-xs !font-medium shrink-0"
                >
                  <span class="material-icons align-middle" style="font-size:11px">error_outline</span>
                  <span>{{ $t('botEditor.flows.isInvalid') }}</span>
                </Chip>
                <Chip
                  v-else-if="flow.isValid === true"
                  class="!bg-green-500/15 !text-green-400 !border !border-green-500/30 !text-xs !font-medium shrink-0"
                >
                  <span class="material-icons align-middle" style="font-size:11px">check_circle</span>
                  <span>{{ $t('botEditor.flows.isValid') }}</span>
                </Chip>
              </div>
              <!-- Date -->
              <p class="text-xs text-gray-500 mt-0.5">
                {{ $t('botEditor.flows.lastModified') }} {{ new Date(flow.updatedAt).toLocaleDateString() }}
              </p>
              <!-- Trigger phrases -->
              <div v-if="getTriggerPhrases(flow).length > 0" class="mt-3 flex flex-wrap gap-1.5">
                <span
                  v-for="(phrase, idx) in getTriggerPhrases(flow)"
                  :key="idx"
                  class="inline-block px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded-md"
                >
                  {{ phrase }}
                </span>
              </div>
              <p v-else class="text-xs text-gray-600 italic mt-2">{{ $t('botEditor.flows.noTriggerPhrases') }}</p>
            </div>
          </div>

          <!-- Right: actions. @click.prevent on the container too: the row is a router-link, and
               browsers disagree on whether a click on the disabled (loading) button bubbles up -->
          <div class="flex items-center gap-2 shrink-0" @click.prevent>
            <!-- The icon goes in the #icon slot (not the default slot) so :loading can swap it
                 for PrimeVue's spinner while this row is being duplicated. A disabled button gets
                 no hover events, so the tooltip sits on a wrapper span and pointer-events-none
                 lets the pointer through to it while the button is disabled -->
            <span v-tooltip.bottom="$t('botEditor.flows.moreActions')" class="inline-flex">
              <Button
                data-telemetry-id="btn-flow-actions"
                severity="secondary"
                text
                aria-haspopup="true"
                class="disabled:pointer-events-none"
                :aria-label="$t('botEditor.flows.moreActions')"
                :loading="duplicatingId === flow._id"
                :disabled="duplicatingId === flow._id"
                @click.prevent="openActionsMenu($event, flow)"
              >
                <template #icon>
                  <span class="material-icons select-none" aria-hidden="true">more_vert</span>
                </template>
              </Button>
            </span>
          </div>
        </div>
      </router-link>
    </div>

    <!-- Row actions menu: a single popup shared by every row, opened for menuFlow -->
    <Menu ref="actionsMenu" :model="actionItems" :popup="true">
      <template #item="{ item, props: itemProps }">
        <!-- Danger items take the same red as a severity="danger" text Button. Inline style,
             not a utility class: PrimeVue's unlayered .p-menu-item-link color rule beats
             Tailwind's layered utilities (cssLayer is off), and a bang override is not wanted -->
        <a
          v-bind="itemProps.action"
          :data-telemetry-id="item.telemetryId"
          class="flex items-center gap-2 select-none"
          :style="item.danger ? { color: 'var(--p-button-text-danger-color)' } : undefined"
        >
          <span class="material-icons text-sm">{{ item.materialIcon }}</span>
          <span>{{ item.label }}</span>
        </a>
      </template>
    </Menu>

    <!-- Create flow. Closing via X / Esc runs closeCreateModal, same as the Cancel button. -->
    <Dialog
      id="create-flow-modal"
      :visible="showCreateModal"
      modal
      :header="$t('botEditor.flows.createModalTitle')"
      :draggable="false"
      :style="{ width: '28rem' }"
      :breakpoints="{ '640px': '95vw' }"
      @update:visible="(visible) => { if (!visible) closeCreateModal(); }"
    >
      <!-- The submit Button lives in the Dialog footer (outside this form), so it
           is tied back to the form with form="create-flow-form"; Enter still submits. -->
      <form id="create-flow-form" class="flex flex-col gap-4" @submit.prevent="handleCreateFlow">
        <div>
          <label for="input-flow-name" class="block text-sm font-medium text-gray-300 mb-2">
            {{ $t('botEditor.flows.flowNameLabel') }}
          </label>
          <InputText
            id="input-flow-name"
            v-model="newFlowName"
            class="w-full"
            :placeholder="$t('botEditor.flows.flowNameLabel')"
            autofocus
            required
          />
        </div>
        <Message v-if="formError" severity="error" :closable="false">{{ formError }}</Message>
      </form>

      <template #footer>
        <Button
          id="btn-cancel-create-flow"
          type="button"
          severity="secondary"
          :label="$t('botEditor.flows.cancelButton')"
          @click="closeCreateModal"
        />
        <Button
          id="btn-confirm-create-flow"
          type="submit"
          form="create-flow-form"
          severity="primary"
          :label="isSubmitting ? $t('botEditor.flows.creatingButton') : $t('botEditor.flows.createFlowButton')"
          :loading="isSubmitting"
          :disabled="isSubmitting"
        />
      </template>
    </Dialog>

    <!-- Delete confirmation: stays open and locked (no Cancel / X / Esc / mask) while
         isDeleting; confirmDelete closes it when the request settles. -->
    <ConfirmActionDialog
      id="delete-flow-modal"
      v-model:visible="showDeleteModal"
      severity="danger"
      icon="warning"
      :title="$t('botEditor.flows.deleteModalTitle')"
      :message="$t('botEditor.flows.deleteModalMessage')"
      :confirm-label="isDeleting ? $t('botEditor.flows.deletingButton') : $t('botEditor.flows.deleteModalConfirm')"
      :cancel-label="$t('botEditor.flows.deleteModalCancel')"
      :loading="isDeleting"
      :confirm-props="{ id: 'btn-confirm-delete-flow' }"
      :cancel-props="{ id: 'btn-cancel-delete-flow' }"
      @confirm="confirmDelete"
      @cancel="closeDeleteModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';
import { showToast } from '../../services/toastService';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';
import ConfirmActionDialog from '../../components/ui/ConfirmActionDialog.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const botId = ref(null);
const workspaceId = computed(() => route.params.workspaceId);

const flows = ref([]);
const loading = ref(true);
const showCreateModal = ref(false);
const newFlowName = ref('');
const isSubmitting = ref(false);
const formError = ref(null);

const showDeleteModal = ref(false);
const flowToDeleteId = ref(null);
const isDeleting = ref(false);

const duplicatingId = ref(null);

const fetchFlows = async () => {
  try {
    loading.value = true;
    const response = await api.get(`/workspaces/${workspaceId.value}/flows/${botId.value}`);
    flows.value = response.data.data;
  } catch (error) {
    Console.error('Errore nel caricamento dei flussi:', error);
  } finally {
    loading.value = false;
  }
};

const getTriggerPhrases = (flow) => {
  if (!flow.definition?.nodes || !Array.isArray(flow.definition.nodes)) return [];
  const startNode = flow.definition.nodes.find(node => node.type === 'input');
  if (!startNode || !startNode.data?.intentPhrases) return [];
  return startNode.data.intentPhrases
    .split('\n')
    .map(t => t.trim())
    .filter(t => t.length > 0);
};

onMounted(() => {
  botId.value = route.params.id;
  fetchFlows();
});

const openCreateModal = () => {
  newFlowName.value = '';
  formError.value = null;
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  showCreateModal.value = false;
};

const handleCreateFlow = async () => {
  isSubmitting.value = true;
  formError.value = null;
  try {
    const response = await api.post(`/workspaces/${workspaceId.value}/flows/${botId.value}`, { name: newFlowName.value });
    const newFlow = response.data.data;
    await fetchFlows();
    closeCreateModal();
    router.push(`/app/workspace/${route.params.workspaceId}/bots/${botId.value}/flows/editor/${newFlow._id}`);
  } catch (error) {
    Console.error('Errore nella creazione del flusso:', error);
    formError.value = error.response?.data?.error?.message || t('botEditor.flows.createErrorMessage');
  } finally {
    isSubmitting.value = false;
  }
};

const openDeleteModal = (id) => {
  flowToDeleteId.value = id;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  flowToDeleteId.value = null;
  showDeleteModal.value = false;
};

const confirmDelete = async () => {
  // Capture the id first: closeDeleteModal() nulls flowToDeleteId
  const id = flowToDeleteId.value;
  if (!id || isDeleting.value) return;
  isDeleting.value = true;
  try {
    await api.delete(`/workspaces/${workspaceId.value}/flows/${id}`);
    flows.value = flows.value.filter(f => f._id !== id);
  } catch (error) {
    Console.error('Errore nella cancellazione del flusso:', error);
    showToast(t('botEditor.flows.deleteErrorMessage'), 'error');
    // Re-sync with the server: the delete may still have completed after a client-side timeout
    await fetchFlows();
  } finally {
    isDeleting.value = false;
    closeDeleteModal();
  }
};

const duplicateFlow = async (flow) => {
  if (!flow || duplicatingId.value) return;
  duplicatingId.value = flow._id;
  try {
    const response = await api.post(
      `/workspaces/${workspaceId.value}/flows/${flow._id}/duplicate`,
      { name: t('botEditor.flows.duplicateName', { name: flow.name }) },
    );
    // The list is newest-first: the copy goes on top, no refetch needed
    flows.value = [response.data.data, ...flows.value];
    showToast(t('botEditor.flows.duplicateSuccessMessage'), 'success');
  } catch (error) {
    Console.error('Errore nella duplicazione del flusso:', error);
    showToast(t('botEditor.flows.duplicateErrorMessage'), 'error');
  } finally {
    duplicatingId.value = null;
  }
};

// Row actions menu: one popup Menu shared by every row; menuFlow is the row it was opened for
const actionsMenu = ref(null);
const menuFlow = ref(null);

const actionItems = computed(() => [
  {
    label: t('botEditor.flows.duplicateAction'),
    materialIcon: 'content_copy',
    telemetryId: 'btn-duplicate-flow',
    // Another row is already being duplicated (duplicateFlow would ignore the click)
    disabled: duplicatingId.value !== null,
    command: () => duplicateFlow(menuFlow.value),
  },
  {
    label: t('common.delete'),
    materialIcon: 'delete',
    telemetryId: 'btn-delete-flow',
    danger: true,
    command: () => openDeleteModal(menuFlow.value._id),
  },
]);

const openActionsMenu = (event, flow) => {
  menuFlow.value = flow;
  actionsMenu.value.toggle(event);
};
</script>
